import { queryOptions, useQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { IntakeHttpClient, type IntakeResponse } from '@omnea-app/intake/intakeHttpClient'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { isAwaitingSuggestedEditsDispatch } from './suggested-edits/suggestedEditsState'

export type GetDocumentReviewResponse = NonNullable<IntakeResponse<'/review/{documentReviewId}', 'get'>['data']>

export const getDocumentReviewQueryKey = (documentReviewId: string | undefined) =>
  ['get-document-review', documentReviewId] as const

export const REVIEW_POLL_INTERVAL_MS = 5_000

type PendingReviewRefetchData = Pick<
  GetDocumentReviewResponse,
  'status' | 'suggestedEditsStatus' | 'suggestedEditsDocumentStatus' | 'findings'
>

type PendingReviewRefetchOptions = {
  considerSuggestedEdits: boolean
  terminalSince: number | null
  now?: number
}

// `considerSuggestedEdits` is per-call, not global: the supplier surface must never poll for them.
// `terminalSince` covers the window before dispatch is claimed, while the status is still null.
export const pendingReviewRefetchInterval = (
  data: PendingReviewRefetchData | undefined,
  { considerSuggestedEdits, terminalSince, now = Date.now() }: PendingReviewRefetchOptions
) => {
  const isDocumentPreparing = data?.suggestedEditsDocumentStatus === 'pending'
  const isAwaitingDispatch =
    considerSuggestedEdits &&
    data !== undefined &&
    isAwaitingSuggestedEditsDispatch({
      suggestedEditsStatus: data.suggestedEditsStatus ?? null,
      hasFailingFinding: data.findings.some((finding) => finding.status === 'fail'),
      terminalSince,
      now,
    })
  const suggestedEditsPending = considerSuggestedEdits && (data?.suggestedEditsStatus === 'pending' || isDocumentPreparing)
  return data?.status === 'pending' || suggestedEditsPending || isAwaitingDispatch ? REVIEW_POLL_INTERVAL_MS : (false as const)
}

type PostSuccessOrPartial = Extract<
  NonNullable<IntakeResponse<'/review-document', 'post'>['data']>,
  { status: 'success' | 'partial' }
>

/**
 * Transforms a successful POST /review-document response into the shape expected by the
 * GET /review/{documentReviewId} query cache, so the card and modal can always read from
 * a single GET query (no POST-vs-GET branching) and skip the network round-trip.
 * Fields unavailable from the POST response are filled with neutral defaults that the UI
 * never reads.
 */
export const seedGetCacheFromPost = (
  postData: PostSuccessOrPartial,
  fileStoreId: string,
  userId: string
): GetDocumentReviewResponse => ({
  id: postData.documentReviewId,
  initiatedBy: userId,
  requestId: null,
  fileStoreId,
  fileName: null,
  playbookId: postData.playbookId,
  rulesSnapshot: [],
  findings: postData.findings ?? [],
  outcome: postData.outcome,
  summary: postData.summary ?? null,
  status: postData.status,
  error: postData.status === 'partial' ? (postData.error ?? null) : null,
  createdAt: postData.reviewedAt,
  suggestedEditsStatus: null,
  suggestedEditsResult: null,
  suggestedEditsDispatchedAt: null,
  suggestedEditsDocumentStatus: null,
  suggestedEditsDocumentFileStoreId: null,
  suggestedEditsRevisions: [],
})

type DocumentReviewQueryParams = {
  documentReviewId: string | undefined
  getToken: () => Promise<string>
}

export const documentReviewQueryOptions = ({ documentReviewId, getToken }: DocumentReviewQueryParams) =>
  queryOptions({
    queryKey: getDocumentReviewQueryKey(documentReviewId),
    queryFn: async () => {
      if (!documentReviewId) throw new Error('documentReviewId is required')

      const authorization = await getToken()
      const intakeClient = new IntakeHttpClient({ authorization })

      const { data, error } = await intakeClient.GET('/review/{documentReviewId}', {
        params: { path: { documentReviewId } },
      })

      throwIfApiError(error)
      return data
    },
    enabled: documentReviewId !== undefined,
    // A cache write is only ever refreshed by refetchInterval below, never by remount-triggered
    // staleness, so infinite staleTime is safe even while suggested-edits polling stays active.
    staleTime: Infinity,
    retry: false,
    // Suggested-edits polling needs a stamp held across renders, so `useGetDocumentReview` overrides
    // this for the callers that opt in. On its own the query only follows the review's own status.
    refetchInterval: (query) =>
      pendingReviewRefetchInterval(query.state.data, { considerSuggestedEdits: false, terminalSince: null }),
  })

export type UseGetDocumentReviewOptions = {
  /** Keep polling while a suggested-edits step is pending too, not just the top-level review status. Off by default. */
  considerSuggestedEditsInPoll?: boolean
}

export const useGetDocumentReview = (documentReviewId: string | undefined, options: UseGetDocumentReviewOptions = {}) => {
  const { considerSuggestedEditsInPoll = false } = options
  const { getToken } = useAuthenticatedUser()
  // Stamped the first time the review reads as terminal, anchoring the dispatch window.
  const terminalSinceRef = useRef<number | null>(null)
  // A persisted component can point this hook at a new documentReviewId without unmounting — reset
  // so a stamp from the previous review can't suppress or shorten this one's window.
  const previousDocumentReviewIdRef = useRef(documentReviewId)
  if (previousDocumentReviewIdRef.current !== documentReviewId) {
    previousDocumentReviewIdRef.current = documentReviewId
    terminalSinceRef.current = null
  }

  return useQuery({
    ...documentReviewQueryOptions({ documentReviewId, getToken }),
    refetchInterval: (query) => {
      const data = query.state.data
      if (considerSuggestedEditsInPoll && data !== undefined && data.status !== 'pending' && terminalSinceRef.current === null) {
        terminalSinceRef.current = Date.now()
      }
      return pendingReviewRefetchInterval(data, {
        considerSuggestedEdits: considerSuggestedEditsInPoll,
        terminalSince: terminalSinceRef.current,
      })
    },
  })
}
